'use client';

import React, { useState, useEffect } from 'react';
import { Button } from '@/shared/components/ui/button';
import Image from 'next/image';
import { sendBookingRequest } from '@/shared/lib/booking-api';

interface UniversalBookingModalProps {
    isOpen: boolean;
    onClose: () => void;
    blockName?: string;
}

interface FormData {
    date: string;
    services: string[];
    phone: string;
}

interface ValidationErrors {
    date?: string;
    phone?: string;
}

const services = [
    'Замена масла в ДВС',
    'Замена масляного фильтра',
    'Замена воздушного фильтра',
    'Замена масла в АКПП',
    'Замена тормозной жидкости',
    'Смена шин'
];

export default function UniversalBookingModal({
    isOpen,
    onClose,
    blockName = 'Универсальная форма записи',
}: UniversalBookingModalProps) {
    const [formData, setFormData] = useState<FormData>({
        date: '',
        services: [],
        phone: '',
    });
    
    const [customService, setCustomService] = useState('');
    const [privacyAgreed, setPrivacyAgreed] = useState(false);
    const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);

    const getInputNumbersValue = (input: HTMLInputElement) => {
        return input.value.replace(/\D/g, '');
    };

    const onPhonePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        let input = e.currentTarget;
        let inputNumbersValue = getInputNumbersValue(input);
        let pasted = e.clipboardData;
        if (pasted) {
            let pastedText = pasted.getData('Text');
            if (/\D/g.test(pastedText)) {
                input.value = inputNumbersValue;
                return;
            }
        }
    };

    const onPhoneInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        let input = e.currentTarget;
        let inputNumbersValue = getInputNumbersValue(input);
        let selectionStart = input.selectionStart || 0;
        let formattedInputValue = '';

        if (!inputNumbersValue) {
            input.value = '';
            return;
        }

        if (input.value.length !== selectionStart) {
            const event = e.nativeEvent as InputEvent;
            if (event.data && /\D/g.test(event.data)) {
                input.value = inputNumbersValue;
            }
            return;
        }

        if (['7', '8', '9'].indexOf(inputNumbersValue[0]) > -1) {
            if (inputNumbersValue[0] === '9') inputNumbersValue = '7' + inputNumbersValue;
            let firstSymbols = inputNumbersValue[0] === '8' ? '8' : '+7';
            formattedInputValue = firstSymbols + ' ';
            if (inputNumbersValue.length > 1) {
                formattedInputValue += '(' + inputNumbersValue.substring(1, 4);
            }
            if (inputNumbersValue.length >= 5) {
                formattedInputValue += ') ' + inputNumbersValue.substring(4, 7);
            }
            if (inputNumbersValue.length >= 8) {
                formattedInputValue += '-' + inputNumbersValue.substring(7, 9);
            }
            if (inputNumbersValue.length >= 10) {
                formattedInputValue += '-' + inputNumbersValue.substring(9, 11);
            }
        } else {
            formattedInputValue = '+' + inputNumbersValue.substring(0, 16);
        }
        input.value = formattedInputValue;
        setFormData(prev => ({ ...prev, phone: formattedInputValue }));
        
        // Очищаем ошибку валидации при вводе
        if (validationErrors.phone) {
            setValidationErrors(prev => ({ ...prev, phone: undefined }));
        }
    };

    const onPhoneFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        const input = e.currentTarget;
        if (!input.value) {
            input.value = '+7 ';
            setFormData(prev => ({ ...prev, phone: '+7 ' }));
        }
    };

    const onPhoneKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        let inputValue = e.currentTarget.value.replace(/\D/g, '');
        if (e.key === 'Backspace' && inputValue.length === 1) {
            e.currentTarget.value = '';
            setFormData(prev => ({ ...prev, phone: '' }));
        }
    };

    const validateDate = (dateString: string): string | null => {
        if (dateString.length !== 10) {
            return 'Введите полную дату';
        }

        const parts = dateString.split('.');
        if (parts.length !== 3) {
            return 'Неверный формат даты';
        }

        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10);
        const year = parseInt(parts[2], 10);

        if (isNaN(day) || isNaN(month) || isNaN(year)) {
            return 'Неверный формат даты';
        }

        if (month < 1 || month > 12) {
            return 'Месяц должен быть от 1 до 12';
        }

        if (day < 1 || day > 31) {
            return 'День должен быть от 1 до 31';
        }

        // Названия месяцев в предложном падеже
        const monthNames = [
            'январе', 'феврале', 'марте', 'апреле', 'мае', 'июне',
            'июле', 'августе', 'сентябре', 'октябре', 'ноябре', 'декабре'
        ];

        // Проверка количества дней в месяце
        const daysInMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
        
        // Проверка високосного года
        const isLeapYear = (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
        if (isLeapYear) {
            daysInMonth[1] = 29;
        }

        if (day > daysInMonth[month - 1]) {
            return `В ${monthNames[month - 1]} не может быть ${day} дней`;
        }

        const currentYear = new Date().getFullYear();
        const maxYear = currentYear + 5; // Максимум на 5 лет вперёд
        if (year < currentYear || year > maxYear) {
            return `Год должен быть от ${currentYear} до ${maxYear}`;
        }

        // Проверка, что дата не в прошлом
        const selectedDate = new Date(year, month - 1, day);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        if (selectedDate < today) {
            return 'Нельзя выбрать прошедшую дату';
        }

        return null;
    };

    const onDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let input = e.currentTarget;
        let value = input.value.replace(/\D/g, '');
        let formattedValue = '';

        if (value.length > 0) {
            formattedValue = value.substring(0, 2);
        }
        if (value.length >= 3) {
            formattedValue += '.' + value.substring(2, 4);
        }
        if (value.length >= 5) {
            formattedValue += '.' + value.substring(4, 8);
        }

        input.value = formattedValue;
        setFormData(prev => ({ ...prev, date: formattedValue }));
        
        if (formattedValue.length === 10) {
            const error = validateDate(formattedValue);
            setValidationErrors(prev => ({ ...prev, date: error || undefined }));
        } else {
            setValidationErrors(prev => ({ ...prev, date: undefined }));
        }
    };

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
            setFormData({
                date: '',
                services: [],
                phone: '',
            });
            setCustomService('');
            setPrivacyAgreed(false);
            setValidationErrors({});
            setIsSubmitting(false);
            setSubmitSuccess(false);
            setShowSuccessModal(false);
        } else {
            document.body.style.overflow = 'unset';
        }

        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    const validatePhone = (phone: string): string | null => {
        const phoneDigits = phone.replace(/\D/g, '');
        if (!phone || phoneDigits.length < 11) {
            return 'Введите корректный номер телефона';
        }
        return null;
    };

    const handleServiceToggle = (service: string) => {
        const isCurrentlySelected = formData.services.includes(service);
        let newServices: string[];
        
        if (isCurrentlySelected) {
            newServices = formData.services.filter(s => s !== service);
        } else {
            newServices = [...formData.services, service];
        }
        
        setFormData(prev => ({
            ...prev,
            services: newServices
        }));
        
        setCustomService(newServices.join(', '));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!privacyAgreed) {
            return;
        }
        
        const dateError = validateDate(formData.date);
        if (dateError) {
            setValidationErrors(prev => ({ ...prev, date: dateError }));
            return;
        }
        
        const phoneError = validatePhone(formData.phone);
        if (phoneError) {
            setValidationErrors(prev => ({ ...prev, phone: phoneError }));
            return;
        }
        
        setIsSubmitting(true);
        setSubmitSuccess(false);
        
        const result = await sendBookingRequest({
            phone: formData.phone,
            date: formData.date,
            services: formData.services,
            customService: customService,
            blockName: blockName,
        });
        
        if (result.success) {
            setSubmitSuccess(true);
            setShowSuccessModal(true);

            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({'event':'form_onlinereg'});
        } else {
            alert(result.error || 'Произошла ошибка при отправке формы. Пожалуйста, попробуйте еще раз.');
        }
        
        setIsSubmitting(false);
    };

    const handleOverlayClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70"
            onClick={handleOverlayClick}
        >
            <div 
                className="bg-white rounded-[20px] shadow-xl w-full max-w-[500px] max-h-[90vh] overflow-hidden flex flex-col relative z-10"
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
            >
                {/* Фиксированный заголовок с изображением */}
                <div className="relative flex-shrink-0">
                    {/* Кнопка закрытия */}
                    <button
                        onClick={onClose}
                        className="absolute top-4 right-4 z-10 text-white hover:text-gray-200 transition-colors"
                    >
                        <Image src="/images/close.svg" width={24} height={24} alt="Закрыть" />
                    </button>

                    {/* Фоновое изображение с текстом */}
                    <div className="relative h-[280px] sm:h-[320px] rounded-t-[20px] overflow-hidden">
                        <Image
                            src="/images/universal-form.jpg"
                            alt="Форма записи"
                            fill
                            className="object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-b from-transparent from-0% to-[rgba(40,40,40,0.7)] to-100% pointer-events-none" />
                        
                        <div className="absolute inset-0 flex flex-col items-center justify-end text-center px-6 pb-6 sm:pb-8 pointer-events-none">
                            <h2 className="text-[28px] sm:text-[36px] lg:text-[42px] font-bold text-white leading-[1.1] mb-3 sm:mb-4">
                                Добро пожаловать!
                            </h2>
                            <p className="text-[14px] sm:text-[16px] text-white leading-[1.3] max-w-[380px]">
                                Укажите желаемые даты обслуживания, услуги и ваш контакт, и мы свяжемся для уточнения деталей
                            </p>
                        </div>
                    </div>
                </div>

                {/* Прокручиваемая форма */}
                <div className="overflow-y-auto flex-1 relative">
                    <form onSubmit={handleSubmit} className="p-6 space-y-4">
                    {/* Поле даты */}
                    <div className="bg-custom-ig rounded-lg lg:rounded-2xl p-4 lg:p-5">
                        <div className="text-custom-orange font-bold text-[14px] lg:text-[16px] uppercase mb-3 leading-tight">
                            Желаемая дата
                        </div>
                        <input
                            type="text"
                            placeholder="дд.мм.гггг"
                            maxLength={10}
                            value={formData.date}
                            onChange={onDateChange}
                            required
                            className={`w-full bg-white border-1 rounded-[5px] px-4 py-3 text-[16px] lg:text-[18px] text-custom-black focus:border-custom-orange outline-none ${
                                validationErrors.date ? 'border-red-500' : 'border-custom-slight-gray'
                            }`}
                        />
                        {validationErrors.date && (
                            <div className="text-red-500 text-[12px] lg:text-[14px] mt-2 leading-tight">
                                {validationErrors.date}
                            </div>
                        )}
                    </div>

                    {/* Выбор услуг */}
                    <div className="bg-custom-ig rounded-lg lg:rounded-2xl p-4 lg:p-5">
                        <div className="text-custom-orange font-bold text-[14px] lg:text-[16px] uppercase mb-3 leading-tight">
                            УКАЖИТЕ УСЛУГУ
                        </div>
                        <div className="space-y-2">
                            {services.map((service, index) => (
                                <label
                                    key={index}
                                    className="flex items-center cursor-pointer group"
                                >
                                    <input
                                        type="checkbox"
                                        checked={formData.services.includes(service)}
                                        onChange={() => handleServiceToggle(service)}
                                        className="mr-3 w-4 h-4 lg:w-5 lg:h-5 cursor-pointer accent-custom-gray border-custom-light rounded focus:ring-custom-gray hover:scale-110 transition-transform"
                                    />
                                    <span className="text-[14px] lg:text-[16px] text-custom-black group-hover:text-custom-orange transition-colors leading-[1.3]">
                                        {service}
                                    </span>
                                </label>
                            ))}
                        </div>
                        <div className="mt-4">
                            <div className="text-custom-orange font-bold text-[14px] lg:text-[16px] uppercase mb-3 leading-tight">
                                ИЛИ УКАЖИТЕ УСЛУГУ ВРУЧНУЮ
                            </div>
                            <textarea
                                value={customService}
                                onChange={(e) => {
                                    const newValue = e.target.value;
                                    setCustomService(newValue);
                                    // Обновляем services из текстового поля
                                    const servicesFromText = newValue
                                        .split(',')
                                        .map(s => s.trim())
                                        .filter(s => s.length > 0);
                                    setFormData(prev => ({
                                        ...prev,
                                        services: servicesFromText,
                                    }));
                                }}
                                placeholder="Введите услуги через запятую"
                                className="w-full bg-white border-1 border-custom-slight-gray rounded-[5px] px-4 py-3 text-[14px] lg:text-[16px] text-custom-black focus:border-custom-orange outline-none resize-none"
                                rows={3}
                            />
                        </div>
                    </div>

                    {/* Поле телефона */}
                    <div className="bg-custom-ig rounded-lg lg:rounded-2xl p-4 lg:p-5">
                        <div className="text-custom-orange font-bold text-[14px] lg:text-[16px] uppercase mb-3 leading-tight">
                            ВАШ КОНТАКТ
                        </div>
                        <input
                            type="tel"
                            placeholder="+7 (XXX) XXX-XX-XX"
                            value={formData.phone}
                            onFocus={onPhoneFocus}
                            onChange={onPhoneInput}
                            onPaste={onPhonePaste}
                            onKeyDown={onPhoneKeyDown}
                            required
                            className={`w-full bg-white border-1 rounded-[5px] px-4 py-3 text-[16px] lg:text-[18px] text-custom-black focus:border-custom-orange outline-none ${
                                validationErrors.phone ? 'border-red-500' : 'border-custom-slight-gray'
                            }`}
                        />
                        {validationErrors.phone && (
                            <div className="text-red-500 text-[12px] lg:text-[14px] mt-2 leading-tight">
                                {validationErrors.phone}
                            </div>
                        )}
                    </div>

                    {/* Чекбокс согласия с политикой */}
                    <div className="bg-custom-ig rounded-lg lg:rounded-2xl p-4 lg:p-5">
                        <label className="flex items-start cursor-pointer group">
                            <input
                                type="checkbox"
                                checked={privacyAgreed}
                                onChange={(e) => setPrivacyAgreed(e.target.checked)}
                                required
                                className="mr-3 mt-1 w-4 h-4 lg:w-5 lg:h-5 cursor-pointer accent-custom-gray border-custom-light rounded focus:ring-custom-gray hover:scale-110 transition-transform flex-shrink-0"
                            />
                            <span className="text-[14px] lg:text-[16px] text-custom-black leading-[1.3]">
                                Я согласен с{' '}
                                <a
                                    href="/privacy-policy.pdf"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-custom-orange hover:underline"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    политикой обработки персональных данных
                                </a>
                            </span>
                        </label>
                    </div>

                    {/* Кнопка отправки */}
                    <Button
                        type="submit"
                        variant="filledOrange"
                        className="w-full py-4 px-6 text-[16px] lg:text-[18px] font-button font-bold"
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? 'Отправка...' : submitSuccess ? 'Отправлено!' : 'заявка на запись'}
                    </Button>
                    
                </form>
                </div>
            </div>

            {/* Модальное окно успеха */}
            {showSuccessModal && (
                <div
                    className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70"
                    onClick={() => setShowSuccessModal(false)}
                >
                    <div 
                        className="bg-white rounded-[20px] shadow-xl w-full max-w-[500px] p-8 text-center relative"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            onClick={() => {
                                setShowSuccessModal(false);
                                onClose();
                            }}
                            className="absolute top-4 right-4 text-custom-black hover:opacity-80 text-2xl"
                            style={{ fontSize: '28px', lineHeight: '1' }}
                            aria-label="Закрыть"
                        >
                            ×
                        </button>
                        
                        <h3 
                            className="text-[18px] sm:text-[20px] font-medium text-custom-black mb-4"
                        >
                            Заявка на запись сформирована!
                        </h3>
                        
                        <p 
                            className="text-[18px] sm:text-[20px] font-medium text-custom-black leading-relaxed"
                        >
                            Мы свяжемся с Вами для уточнения времени!
                        </p>
                        
                        <p 
                            className="text-[18px] sm:text-[20px] font-medium text-custom-black mb-6"
                        >
                            Спасибо!
                        </p>
                        
                        <button
                            onClick={() => {
                                setShowSuccessModal(false);
                                onClose();
                            }}
                            className="w-full py-3 px-4 text-[18px] font-normal tracking-[-0.02em] bg-custom-orange text-white rounded-[50px] transition-all duration-200 hover:bg-custom-orange/90"
                            style={{ fontFamily: 'var(--font-gothampro, sans-serif)' }}
                        >
                            Закрыть
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
